import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Modal } from '../components/Modal';
import { varinymAPI } from '../lib/varinym-api';
import type { Context } from '../types';

export function Contexts() {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    expires: '0',
    rotates: false,
    format_type: 'uuid' as 'uuid' | 'email' | 'phone',
  });
  const [editingContext, setEditingContext] = useState<Context | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    expires: '0',
    rotates: false,
    active: true,
    format_type: 'uuid' as 'uuid' | 'email' | 'phone',
  });
  const queryClient = useQueryClient();

  const { data: contextsData, isLoading } = useQuery({
    queryKey: ['contexts'],
    queryFn: () => varinymAPI.listContexts(),
  });

  const createMutation = useMutation({
    mutationFn: (context: typeof formData) =>
      varinymAPI.createContext({
        name: context.name,
        expires: parseInt(context.expires),
        rotates: context.rotates,
        active: true, // Always create contexts as active
        format_type: context.format_type,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contexts'] });
      setShowForm(false);
      setFormData({ name: '', expires: '0', rotates: false, format_type: 'uuid' });
      alert('Context created successfully!');
    },
    onError: (err: Error) => {
      alert(`Error: ${err.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ name, context }: { name: string; context: typeof editFormData }) =>
      varinymAPI.updateContext(name, {
        name: context.name,
        expires: parseInt(context.expires),
        rotates: context.rotates,
        active: context.active,
        format_type: context.format_type,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contexts'] });
      setEditingContext(null);
      alert('Context updated successfully!');
    },
    onError: (err: Error) => {
      alert(`Error: ${err.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleEditClick = (context: Context) => {
    setEditingContext(context);
    setEditFormData({
      name: context.name,
      expires: context.expires.toString(),
      rotates: context.rotates,
      active: context.active,
      format_type: context.format_type || 'uuid',
    });
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingContext) {
      updateMutation.mutate({ name: editingContext.name, context: editFormData });
    }
  };

  const handleCloseEditModal = () => {
    setEditingContext(null);
    setEditFormData({ name: '', expires: '0', rotates: false, active: true, format_type: 'uuid' });
  };

  const formatDuration = (seconds: number) => {
    if (seconds === 0) return 'Never';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h`;
    return `${Math.floor(seconds / 60)}m`;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-foreground">Contexts</h1>
        <Button onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Add Context'}
        </Button>
      </div>

      {showForm && (
        <Card title="Create New Context" className="mb-8">
          <form onSubmit={handleSubmit}>
            <div className="space-y-5">
              <Input
                label="Context Name"
                placeholder="enter context name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                helperText="Unique identifier for the context (e.g., facebook, google_ads)"
                className="font-mono"
                required
              />
              <Input
                label="Default Expiration (seconds)"
                type="number"
                placeholder="86400"
                value={formData.expires}
                onChange={(e) => setFormData({ ...formData, expires: e.target.value })}
                helperText="Time until aliases expire (0 = never). 86400 = 24 hours"
                className="font-mono"
                required
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Alias ID Format
                </label>
                <select
                  value={formData.format_type}
                  onChange={(e) => setFormData({ ...formData, format_type: e.target.value as 'uuid' | 'email' | 'phone' })}
                  className="block w-full rounded-md border border-border-control bg-popover text-foreground px-3 py-2 text-sm focus:outline-none focus:border-ring focus:ring-2 focus:ring-ring/40 transition-colors"
                >
                  <option value="uuid">UUID — a1b2c3d4-e5f6-7890-abcd-ef1234567890</option>
                  <option value="email">Email — abc123def456@mask.yourdomain.com</option>
                  <option value="phone">Phone — +882001234567890</option>
                </select>
                <p className="mt-2 text-sm text-muted-foreground">
                  Format for generated alias IDs. All formats work with all endpoints.
                </p>
              </div>
              <label
                htmlFor="rotates"
                className="flex items-start gap-3 rounded-md border border-border-control bg-popover p-4 cursor-pointer hover:border-border-strong transition-colors"
              >
                <input
                  id="rotates"
                  type="checkbox"
                  checked={formData.rotates}
                  onChange={(e) => setFormData({ ...formData, rotates: e.target.checked })}
                  className="mt-0.5 h-4 w-4 rounded border-border-control bg-transparent text-primary accent-[hsl(var(--primary))] focus:ring-2 focus:ring-ring/40 focus:ring-offset-0"
                />
                <span>
                  <span className="block text-sm font-medium text-foreground">Enable auto-rotation</span>
                  <span className="block text-sm text-muted-foreground mt-0.5">
                    When enabled, expired aliases reset their expiry period when read (by the context's expiration duration).
                  </span>
                </span>
              </label>
            </div>
            <div className="mt-6 flex gap-3">
              <Button type="submit" isLoading={createMutation.isPending}>
                Create context
              </Button>
              <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        </div>
      ) : contextsData?.contexts && contextsData.contexts.length > 0 ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[...contextsData.contexts]
            .sort((a, b) => Number(b.active !== false) - Number(a.active !== false))
            .map((context) => {
            // Treat context as active if active is not explicitly set to false
            const isActive = context.active !== false;

            return (
              <Card key={context.id} className={!isActive ? 'opacity-60' : ''}>
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <h3 className={`text-lg font-medium ${!isActive ? 'text-muted-foreground' : 'text-foreground'}`}>
                      {context.name}
                    </h3>
                    {!isActive && (
                      <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                        Inactive
                      </span>
                    )}
                  </div>

                  <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Context ID:</dt>
                      <dd className={`font-medium ${!isActive ? 'text-muted-foreground' : 'text-foreground'}`}>
                        {context.id}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Expires:</dt>
                      <dd className={!isActive ? 'text-muted-foreground' : 'text-foreground'}>
                        {formatDuration(context.expires)}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Auto-Rotation:</dt>
                      <dd className={!isActive ? 'text-muted-foreground' : 'text-foreground'}>
                        {context.rotates ? (
                          <span className={`font-medium ${!isActive ? 'text-muted-foreground' : 'text-success'}`}>
                            Enabled
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Disabled</span>
                        )}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Status:</dt>
                      <dd>
                        {isActive ? (
                          <span className="text-success font-medium">Active</span>
                        ) : (
                          <span className="text-muted-foreground font-medium">Inactive</span>
                        )}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">ID Format:</dt>
                      <dd className={!isActive ? 'text-muted-foreground' : 'text-foreground'}>
                        {context.format_type === 'email' ? 'Email' : context.format_type === 'phone' ? 'Phone' : 'UUID'}
                      </dd>
                    </div>
                  </dl>

                <div className="pt-4 border-t border-border">
                  <div className="text-xs text-muted-foreground">
                    <p>Alias behavior for this context:</p>
                    <ul className="mt-2 space-y-1 list-disc list-inside">
                      <li>
                        {context.expires === 0
                          ? 'Never expires'
                          : `Expires after ${formatDuration(context.expires)}`}
                      </li>
                      <li>
                        {context.rotates
                          ? 'Expired aliases rotate and reset expiry window when read'
                          : 'N/A'}
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Edit Button */}
                {context.name.toLowerCase() !== 'default' && (
                  <div className="pt-4">
                    <Button
                      variant="secondary"
                      onClick={() => handleEditClick(context)}
                      className="w-full"
                    >
                      Edit Context
                    </Button>
                  </div>
                )}
                {context.name.toLowerCase() === 'default' && (
                  <div className="pt-4">
                    <p className="text-xs text-muted-foreground text-center italic">
                      Default context cannot be edited
                    </p>
                  </div>
                )}
              </div>
            </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-foreground mb-2">No Contexts Yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first context to start managing aliases
            </p>
            <Button onClick={() => setShowForm(true)}>Create First Context</Button>
          </div>
        </Card>
      )}

      {/* Edit Context Modal */}
      <Modal
        isOpen={!!editingContext}
        onClose={handleCloseEditModal}
        title={`Edit Context: ${editingContext?.name}`}
        size="md"
      >
        <form onSubmit={handleEditSubmit}>
          {/* Warning Message */}
          <div className="mb-6 bg-warning/10 border border-warning/20 rounded-lg p-4">
            <div className="flex gap-3">
              <TriangleAlert className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-1">Important Notice</h4>
                <p className="text-xs text-muted-foreground">
                  Changes to this context configuration will only affect <strong>new aliases</strong> created after this update.
                  Existing aliases will retain their original settings (expiration time, rotation behavior).
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <Input
              label="Context Name"
              value={editFormData.name}
              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              helperText="Unique identifier for the context"
              required
            />
            <Input
              label="Default Expiration (seconds)"
              type="number"
              value={editFormData.expires}
              onChange={(e) => setEditFormData({ ...editFormData, expires: e.target.value })}
              helperText="Time until aliases expire (0 = never). 86400 = 24 hours"
              required
            />
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Alias ID Format
              </label>
              <select
                value={editFormData.format_type}
                onChange={(e) => setEditFormData({ ...editFormData, format_type: e.target.value as 'uuid' | 'email' | 'phone' })}
                className="mt-1 block w-full rounded-md border border-border-control bg-popover px-3 py-2 text-sm text-foreground focus:outline-none focus:border-ring focus:ring-2 focus:ring-ring/40 focus:ring-offset-0 transition-colors"
              >
                <option value="uuid">UUID (e.g., a1b2c3d4-e5f6-7890-abcd-ef1234567890)</option>
                <option value="email">Email (e.g., abc123def456@mask.yourdomain.com)</option>
                <option value="phone">Phone (e.g., +882001234567890)</option>
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Format for generated alias IDs. All formats work with all endpoints.
              </p>
            </div>
            <div className="flex items-center">
              <input
                id="edit-rotates"
                type="checkbox"
                checked={editFormData.rotates}
                onChange={(e) => setEditFormData({ ...editFormData, rotates: e.target.checked })}
                className="h-4 w-4 rounded border-border-control bg-transparent text-primary accent-[hsl(var(--primary))] focus:ring-2 focus:ring-ring/40 focus:ring-offset-0"
              />
              <label htmlFor="edit-rotates" className="ml-2 block text-sm text-foreground">
                Enable Auto-Rotation
              </label>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              When enabled, expired aliases will reset their expiry period when read
            </p>

            <div className="flex items-center">
              <input
                id="edit-active"
                type="checkbox"
                checked={editFormData.active}
                onChange={(e) => setEditFormData({ ...editFormData, active: e.target.checked })}
                className="h-4 w-4 rounded border-border-control bg-transparent text-primary accent-[hsl(var(--primary))] focus:ring-2 focus:ring-ring/40 focus:ring-offset-0"
              />
              <label htmlFor="edit-active" className="ml-2 block text-sm text-foreground">
                Active
              </label>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Inactive contexts cannot have new aliases created
            </p>
          </div>

          <div className="mt-6 flex gap-3 justify-end">
            <Button type="button" variant="secondary" onClick={handleCloseEditModal}>
              Cancel
            </Button>
            <Button type="submit" isLoading={updateMutation.isPending}>
              Update Context
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
